// Join by room code. The code field normalises as you type (see
// `network/roomOptions.normalizeRoomCode`) so an ambiguous character or a
// pasted "room ABCD" still resolves to a valid attempt.
//
// Like the create screen, this one stays put until the server answers, so a
// wrong code or an unreachable server is shown here rather than stranding the
// player on an empty lobby. On success it hands off to the SAME lobby screen
// the host sees — the roster, the code, and the waiting state are identical
// once you are in the room; only "Start now" is host-only.

import type { MenuScreen, MahjongMenuContext } from '../types.js';
import { ROOM_CODE_LENGTH, isCompleteRoomCode, normalizeRoomCode } from '../../network/roomOptions.js';
import { createScreenShell, createMenuHeader, createCrestButton, el } from '@bn-games/ui-shell';
import { t } from '../../i18n/I18n.js';

/** Server rejection → the message to show. Anything else falls back to a generic. */
function joinErrorKey(code: string | null): string {
  if (code === 'ROOM_NOT_FOUND') return 'online.join.errorNotFound';
  if (code === 'ROOM_FULL') return 'online.join.errorFull';
  return 'online.join.error';
}

export function createOnlineJoinScreen(): MenuScreen {
  let frame: HTMLElement | null = null;
  let root: HTMLElement | null = null;
  let unsubRoom: (() => void) | null = null;
  let code = '';
  let submitted = false;

  function submit(ctx: MahjongMenuContext): void {
    if (!isCompleteRoomCode(code) || submitted) return;
    submitted = true;
    ctx.online.joinRoom(code);
    paint(ctx);
  }

  function onRoomUpdate(ctx: MahjongMenuContext): void {
    if (!submitted) return;
    const info = ctx.online.getRoomInfo();
    if (info.roomCode !== null) {
      submitted = false;
      ctx.navigate('online');
      return;
    }
    if (info.state === 'error') submitted = false;
    paint(ctx);
  }

  function buildCodeField(ctx: MahjongMenuContext): HTMLElement {
    const input = el('input', { class: 'online-code-input' });
    if (input instanceof HTMLInputElement) {
      input.value = code;
      input.maxLength = ROOM_CODE_LENGTH;
      input.autocapitalize = 'characters';
      input.spellcheck = false;
      input.disabled = submitted;
      input.setAttribute('aria-label', t('online.join.codeLabel'));
      input.addEventListener('input', () => {
        const normalized = normalizeRoomCode(input.value);
        // Only repaint when normalisation actually changed the text, so the
        // caret does not jump on every keystroke.
        const changed = normalized !== input.value;
        code = normalized;
        if (changed) input.value = normalized;
        updateSubmitState();
      });
      input.addEventListener('keydown', (e) => {
        if (e instanceof KeyboardEvent && e.key === 'Enter') submit(ctx);
      });
    }
    return el('div', {}, [
      el('div', { class: 'online-label', text: t('online.join.codeLabel') }),
      input,
      el('div', { class: 'online-hint', text: t('online.join.codeHint') }),
    ]);
  }

  let submitButton: HTMLElement | null = null;

  function updateSubmitState(): void {
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = submitted || !isCompleteRoomCode(code);
    }
  }

  function buildStatus(ctx: MahjongMenuContext): HTMLElement | null {
    if (submitted) {
      return el('div', {
        class: 'online-status waiting-dots',
        text: t('online.join.connecting'),
      });
    }
    const info = ctx.online.getRoomInfo();
    if (info.state !== 'error') return null;
    return el('div', {
      class: 'online-status',
      text: info.errorMessage ?? t(joinErrorKey(info.errorCode)),
    });
  }

  function paint(ctx: MahjongMenuContext): void {
    if (root === null) return;
    while (root.firstChild) root.removeChild(root.firstChild);
    const shell = createScreenShell();
    frame = shell.frame;
    const body = shell.body;

    body.appendChild(createMenuHeader({ title: t('online.join.title') }));

    const center = el('div', { class: 'online-center' }, [buildCodeField(ctx)]);
    const status = buildStatus(ctx);
    if (status !== null) center.appendChild(status);

    submitButton = createCrestButton({
      label: t('online.join.submit'),
      primary: true,
      disabled: submitted || !isCompleteRoomCode(code),
      onClick: () => submit(ctx),
    });
    center.appendChild(el('div', { class: 'button-stack' }, [submitButton]));
    body.appendChild(center);

    body.appendChild(
      el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({
          label: t('common.back'),
          small: true,
          onClick: () => {
            ctx.online.leaveRoom();
            ctx.navigate('play');
          },
        }),
      ]),
    );
    root.appendChild(frame);
  }

  return {
    mount(rootEl, ctx) {
      root = rootEl;
      code = '';
      submitted = false;
      unsubRoom = ctx.online.onRoomUpdate(() => onRoomUpdate(ctx));
      paint(ctx);
    },
    unmount() {
      unsubRoom?.();
      unsubRoom = null;
      frame?.remove();
      frame = null;
      root = null;
      submitButton = null;
      code = '';
      submitted = false;
    },
  };
}
